import { useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { common, financedEmissions } from "../../../i18n";
import { BorrowerDetail } from "../../../models/dto/cat15/cat15.types";
import EmissionsKpiCard from "../../../components/cat15/EmissionsKpiCard";
import ChartCard from "../../../components/cat15/ChartCard";
import { formatNumber } from "../format";
import OverviewSelect from "./OverviewSelect";
import AssetClassBars from "./AssetClassBars";
import EmissionTrendChart from "./EmissionTrendChart";
import LoanBalanceChart from "./LoanBalanceChart";
import {
  ALL_YEARS,
  emissionTrendSeries,
  formatMillions,
  lastYears,
  loanBalanceSeries,
  totalOutstanding,
  totalPrincipal,
} from "./borrowerSummary";

interface Props {
  borrower: BorrowerDetail;
}

/** Trend window options; Figma (7475:15215) shows "5 Years" as the default. */
const YEAR_OPTIONS = [3, 5, ALL_YEARS];
const DEFAULT_YEARS = 5;

/** Intensity is tCO₂e per M THB — a small decimal, so it keeps 4 places. */
const formatIntensity = (n: number): string => n.toFixed(4);

/** Borrower Summary tab — Figma 7258:26147 (KPI row 7258:26454, charts 7812:29885). */
const DetailSummary = ({ borrower }: Props) => {
  const { t } = useTranslation([financedEmissions, common], {
    nsMode: "fallback",
  });
  const s = borrower.summary;
  const [years, setYears] = useState<number>(DEFAULT_YEARS);

  const trend = useMemo(() => emissionTrendSeries(borrower), [borrower]);
  const balance = useMemo(() => loanBalanceSeries(borrower), [borrower]);
  const mThb = t("Unit-M-THB");

  const kpis = [
    { title: t("Col-Principal"), value: `${formatMillions(totalPrincipal(borrower.loans))} ${mThb}` },
    { title: t("KPI-Outstanding-Balance"), value: `${formatMillions(totalOutstanding(borrower.loans))} ${mThb}` },
    { title: t("Active-Loans"), value: String(s.activeLoans) },
    { title: t("KPI-Financed-Emissions"), value: `${formatNumber(s.financedEmissions)} tCO₂e` },
    // Figma "1.2"; 0 means no scored loan, which is no score rather than a perfect one.
    { title: t("KPI-Average-DQS"), value: s.weightedDqs > 0 ? s.weightedDqs.toFixed(1) : "—" },
  ];

  return (
    <Box display="flex" flexDirection="column" gap="20px">
      {/* KPI row 7258:26454 — five Card/Stat_Card, 16px apart */}
      <Box display="flex" gap="16px">
        {kpis.map((k) => (
          <Box key={k.title} flex={1} minWidth={0}>
            <EmissionsKpiCard
              title={k.title}
              value={k.value}
              titleMinHeight={24}
              rowGap={4}
            />
          </Box>
        ))}
      </Box>

      {/* Chart rows 7812:29885 */}
      <Box display="flex" gap="20px" alignItems="stretch">
        <Box flex={1} minWidth={0}>
          <ChartCard
            title={t("Chart-Financial-Emission-Trend")}
            info={t("Chart-Financial-Emission-Trend-Info")}
            largeInfo
            size="h3"
            bordered
            gap="12px"
            action={
              <OverviewSelect
                value={String(years)}
                onChange={(v) => setYears(Number(v))}
                ariaLabel={t("Trend-Years-Label")}
                options={YEAR_OPTIONS.map((n) => ({
                  value: String(n),
                  label:
                    n === ALL_YEARS
                      ? t("Trend-All-Years")
                      : t("Trend-Years", { count: n }),
                }))}
              />
            }
          >
            <EmissionTrendChart
              points={lastYears(trend, years)}
              unit="tCO₂e"
              barLabel={t("Legend-Financial-Emission")}
              lineLabel={t("Legend-Emission-Intensity")}
              formatIntensity={formatIntensity}
            />
          </ChartCard>
        </Box>
        <Box flex={1} minWidth={0}>
          <ChartCard
            title={t("Chart-Financed-Emission-By-Asset-Class")}
            size="h3"
            bordered
            gap="20px"
            fill
          >
            <AssetClassBars rows={s.byAssetClass} />
          </ChartCard>
        </Box>
      </Box>

      <ChartCard
        title={t("Chart-Loan-Balance")}
        info={t("Chart-Loan-Balance-Info")}
        largeInfo
        size="h3"
        bordered
        gap="12px"
      >
        <LoanBalanceChart
          points={balance}
          principalLabel={t("Axis-Principal-M-THB")}
          outstandingLabel={t("Axis-Outstanding-M-THB")}
        />
      </ChartCard>
    </Box>
  );
};

export default DetailSummary;
